/**
 * Admin page for managing waitlist signups and invite codes
 * Story 265: Gate CLI installation with invite code system
 */

import { useEffect, useState, useCallback } from 'react';
import { config } from '../config';
import './SignupsAdmin.css';

interface Signup {
  id: number;
  email: string;
  reason: string | null;
  status: string;
  created_at: string;
}

interface InviteCode {
  id: number;
  code: string;
  created_at: string;
  active: number;
}

interface UserInfo {
  githubUsername: string;
  email: string;
}

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

export function SignupsAdmin() {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [signups, setSignups] = useState<Signup[]>([]);
  const [inviteCodes, setInviteCodes] = useState<InviteCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [newCode, setNewCode] = useState('');
  const [isCreatingCode, setIsCreatingCode] = useState(false);

  // Fetch user info
  useEffect(() => {
    async function fetchUser() {
      try {
        const response = await fetch(`${config.apiUrl}/auth/me`, {
          credentials: 'include',
        });
        const json = await response.json();
        if (json.authenticated) {
          setUser({
            githubUsername: json.githubUsername,
            email: json.email,
          });
        }
      } catch {
        // Ignore fetch errors
      }
    }
    fetchUser();
  }, []);

  // Fetch signups and invite codes
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const signupsUrl = statusFilter === 'all'
        ? `${config.apiUrl}/api/v1/admin/signups`
        : `${config.apiUrl}/api/v1/admin/signups?status=${statusFilter}`;

      const [signupsRes, codesRes] = await Promise.all([
        fetch(signupsUrl, { credentials: 'include' }),
        fetch(`${config.apiUrl}/api/v1/admin/invite-codes`, { credentials: 'include' }),
      ]);

      if (!signupsRes.ok || !codesRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const signupsData = await signupsRes.json();
      const codesData = await codesRes.json();

      setSignups(signupsData.signups || []);
      setInviteCodes(codesData.codes || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSignOut = useCallback(async () => {
    try {
      await fetch(`${config.apiUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Logout may fail if session already expired
    }
    window.location.href = '/';
  }, []);

  const handleUpdateSignupStatus = useCallback(async (id: number, status: string) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/v1/admin/signups/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        throw new Error('Failed to update signup');
      }

      // Refresh data
      fetchData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Update failed');
    }
  }, [fetchData]);

  const handleToggleCodeActive = useCallback(async (id: number, currentActive: number) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/v1/admin/invite-codes/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: currentActive === 0 }),
      });

      if (!response.ok) {
        throw new Error('Failed to update invite code');
      }

      // Refresh data
      fetchData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Update failed');
    }
  }, [fetchData]);

  const handleCreateCode = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCode.trim()) return;

    setIsCreatingCode(true);
    try {
      const response = await fetch(`${config.apiUrl}/api/v1/admin/invite-codes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: newCode.trim() }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create invite code');
      }

      setNewCode('');
      fetchData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setIsCreatingCode(false);
    }
  }, [newCode, fetchData]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="signups-admin">
      <nav className="signups-admin__nav">
        <div className="signups-admin__nav-inner">
          <div className="signups-admin__nav-brand">
            <a href="/dashboard" className="signups-admin__logo-link">
              <img
                src="/ceetrix-green.png"
                alt="Ceetrix"
                className="signups-admin__logo"
              />
            </a>
            <span className="signups-admin__badge">Admin</span>
          </div>
          <div className="signups-admin__nav-actions">
            {user && (
              <span className="signups-admin__username">
                @{user.githubUsername}
              </span>
            )}
            <button
              onClick={handleSignOut}
              className="signups-admin__sign-out"
              type="button"
            >
              Sign Out
            </button>
          </div>
        </div>
      </nav>

      <main className="signups-admin__main">
        {/* Invite Codes Section */}
        <section className="signups-admin__section">
          <h2 className="signups-admin__title">Invite Codes</h2>
          <form onSubmit={handleCreateCode} className="signups-admin__create-form">
            <input
              type="text"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              placeholder="New invite code"
              className="signups-admin__input"
              minLength={6}
            />
            <button
              type="submit"
              disabled={isCreatingCode || !newCode.trim()}
              className="signups-admin__create-btn"
            >
              {isCreatingCode ? 'Creating...' : 'Create Code'}
            </button>
          </form>

          {inviteCodes.length > 0 ? (
            <table className="signups-admin__table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {inviteCodes.map((code) => (
                  <tr key={code.id}>
                    <td className="signups-admin__code">{code.code}</td>
                    <td>{formatDate(code.created_at)}</td>
                    <td>
                      <span className={`signups-admin__status signups-admin__status--${code.active ? 'active' : 'inactive'}`}>
                        {code.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => handleToggleCodeActive(code.id, code.active)}
                        className="signups-admin__action-btn"
                      >
                        {code.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="signups-admin__empty">No invite codes</p>
          )}
        </section>

        {/* Signups Section */}
        <section className="signups-admin__section">
          <div className="signups-admin__header">
            <h2 className="signups-admin__title">Waitlist Signups</h2>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="signups-admin__filter"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {isLoading && (
            <div className="signups-admin__loading">Loading...</div>
          )}

          {error && (
            <div className="signups-admin__error">{error}</div>
          )}

          {!isLoading && !error && signups.length > 0 ? (
            <table className="signups-admin__table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Signed Up</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {signups.map((signup) => (
                  <tr key={signup.id}>
                    <td>{signup.email}</td>
                    <td className="signups-admin__reason">{signup.reason || '—'}</td>
                    <td>
                      <span className={`signups-admin__status signups-admin__status--${signup.status}`}>
                        {signup.status}
                      </span>
                    </td>
                    <td>{formatDate(signup.created_at)}</td>
                    <td className="signups-admin__actions">
                      {signup.status !== 'approved' && (
                        <button
                          onClick={() => handleUpdateSignupStatus(signup.id, 'approved')}
                          className="signups-admin__action-btn signups-admin__action-btn--approve"
                        >
                          Approve
                        </button>
                      )}
                      {signup.status !== 'rejected' && (
                        <button
                          onClick={() => handleUpdateSignupStatus(signup.id, 'rejected')}
                          className="signups-admin__action-btn signups-admin__action-btn--reject"
                        >
                          Reject
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : !isLoading && !error ? (
            <p className="signups-admin__empty">
              {statusFilter === 'all' ? 'No signups yet' : `No ${statusFilter} signups`}
            </p>
          ) : null}
        </section>
      </main>
    </div>
  );
}
